import { useCallback, useEffect, useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { CheckCircle2Icon, ChevronDownIcon, CircleIcon, MailIcon, RefreshCwIcon, TriangleAlertIcon } from 'lucide-react'
import type { DnsRecord, DnsView } from '@hedgehog/shared'
import { api, ApiRequestError } from '@/lib/api'
import { CopyButton } from '@/components/app/copy-button'
import { formatTime } from '@/components/app/format'
import { OrderError, TabGate } from '@/components/app/order'
import { LoadingState, errorMessage } from '@/components/app/states'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Spinner } from '@/components/ui/spinner'
import { useInterval } from '@/hooks/use-interval'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/o/$orderId/domain')({
  loader: ({ params, abortController }) =>
    api<DnsView>(`/orders/${encodeURIComponent(params.orderId)}/dns`, { signal: abortController.signal }),
  pendingComponent: () => <LoadingState label="Reading your domain settings" />,
  errorComponent: ({ error }) => (
    <TabGate tab="/o/$orderId/domain">
      <OrderError error={error} title="We could not read your domain settings" />
    </TabGate>
  ),
  component: () => (
    <TabGate tab="/o/$orderId/domain">
      <Domain />
    </TabGate>
  ),
})

function Domain() {
  const loaded = Route.useLoaderData()
  const { orderId } = Route.useParams()
  const router = useRouter()
  const [view, setView] = useState<DnsView>(loaded)
  const [checking, setChecking] = useState(false)
  const [checkError, setCheckError] = useState<string | null>(null)
  useEffect(() => setView(loaded), [loaded])

  const check = useCallback(
    async (manual: boolean) => {
      if (manual) setChecking(true)
      try {
        const next = await api<DnsView>(`/orders/${encodeURIComponent(orderId)}/dns/check`, { method: 'POST' })
        setView(next)
        setCheckError(null)
        // Going live changes the order state; refresh the layout so tabs and status follow.
        if (next.status.live && !view.status.live) void router.invalidate()
      } catch (err) {
        if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) void router.invalidate()
        else if (manual) setCheckError(errorMessage(err))
      } finally {
        if (manual) setChecking(false)
      }
    },
    [orderId, router, view.status.live],
  )

  useInterval(() => void check(false), view.status.live ? null : 30000)

  const provider = view.dnsHost ?? view.registrar
  const bare = view.domain
  const records: Array<DnsRecord & { note?: string }> = [
    { type: 'CNAME', name: hostLabel(view.hostname, bare), value: view.target.cname },
  ]
  if (view.target.apexStrategy === 'alias') {
    records.push({
      type: 'ALIAS',
      name: '@',
      value: view.target.cname,
      note: 'Some providers call this ANAME or a flattened CNAME. "@" means the bare domain.',
    })
  }

  return (
    <div className="flex flex-col gap-8">
      <section aria-labelledby="intro" className="flex flex-col gap-2">
        <h2 id="intro" className="text-xl font-semibold">
          Connect {bare} to your new site
        </h2>
        <p className="text-muted-foreground">
          You make one change in your domain settings{provider ? ` at ${provider}` : ''}. A DNS record is a line in those
          settings that tells browsers where your website lives; you point it at us, and your new site appears at your
          address.
        </p>
      </section>

      <div role="note" className="flex gap-3 rounded-xl border-2 border-warning/40 bg-warning/5 p-4">
        <MailIcon className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
        <div className="flex flex-col gap-1">
          <p className="font-semibold">Your email will not change</p>
          <p className="text-sm">
            Do not edit or delete MX records or any record not listed below. MX records deliver your email; leaving them as
            they are keeps your inbox working.
          </p>
        </div>
      </div>

      <Status view={view} checking={checking} onCheck={() => void check(true)} error={checkError} />

      {view.instructions.length > 0 && (
        <section aria-labelledby="steps" className="flex flex-col gap-3">
          <h2 id="steps" className="text-xl font-semibold">
            Step by step{provider ? ` for ${provider}` : ''}
          </h2>
          <ol className="flex flex-col gap-4">
            {view.instructions.map((step, i) => (
              <li key={step.title} className="flex gap-3">
                <span
                  aria-hidden
                  className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground"
                >
                  {i + 1}
                </span>
                <div className="flex flex-col gap-1 pt-0.5">
                  <p className="font-medium">{step.title}</p>
                  <p className="text-sm whitespace-pre-line text-muted-foreground">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section aria-labelledby="records" className="flex flex-col gap-3">
        <h2 id="records" className="text-xl font-semibold">
          {records.length === 1 ? 'The record to add' : 'The records to add'}
        </h2>
        <p className="text-sm text-muted-foreground">
          Copy each value exactly. If a record with the same name already exists, edit it instead of adding a second one.
        </p>
        <ul className="flex flex-col gap-3">
          {records.map((r) => (
            <li key={`${r.type}-${r.name}`} className="rounded-xl border border-border">
              <dl className="divide-y divide-border">
                <RecordRow label="Type" value={r.type} />
                <RecordRow label="Name (or Host)" value={r.name} hint={r.name === 'www' ? `Some providers want the full name: ${view.hostname}` : undefined} />
                <RecordRow label="Value (or Target)" value={r.value} />
              </dl>
              {r.note && <p className="border-t border-border px-4 py-2 text-sm text-muted-foreground">{r.note}</p>}
            </li>
          ))}
        </ul>
        {view.target.apexStrategy === 'redirect' && (
          <p className="rounded-xl border border-border p-4 text-sm">
            <span className="font-medium">For the bare domain ({bare}):</span> your provider cannot point it at us directly. In
            the same settings, turn on domain forwarding from <strong>{bare}</strong> to{' '}
            <strong>https://{view.hostname}</strong> (a permanent redirect, sometimes called 301).
          </p>
        )}
      </section>

      <CurrentRecords view={view} />

      <p className="text-sm text-muted-foreground">
        Keep your old hosting for 30 days after your new site is live, in case we need it. We remind you when you can cancel
        it.
      </p>
    </div>
  )
}

/** Name to type in the registrar's host field: "www" for www.example.com, "@" for the bare domain. */
function hostLabel(hostname: string, domain: string): string {
  if (hostname === domain) return '@'
  return hostname.endsWith(`.${domain}`) ? hostname.slice(0, -domain.length - 1) : hostname
}

function RecordRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
      <dt className="text-sm text-muted-foreground sm:w-40 sm:shrink-0">{label}</dt>
      <dd className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 rounded-md bg-muted px-2 py-1 font-mono text-sm break-all">{value}</code>
          <CopyButton value={value} label={label.toLowerCase()} />
        </div>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </dd>
    </div>
  )
}

function Status({
  view,
  checking,
  onCheck,
  error,
}: {
  view: DnsView
  checking: boolean
  onCheck: () => void
  error: string | null
}) {
  const ssl = view.status.sslStatus === 'active'
  const items: Array<{ done: boolean; label: string; pending: string }> = [
    { done: view.status.pointsToUs, label: 'Your domain points to us', pending: 'Waiting for your DNS change. It can take up to a few hours to be seen.' },
    { done: ssl, label: 'Security certificate issued', pending: 'Issued automatically once your domain points to us.' },
    { done: view.status.live, label: 'Your new site is live', pending: 'Happens right after the certificate.' },
  ]
  return (
    <section aria-labelledby="status" className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="status" className="text-lg font-semibold">
          Connection status
        </h2>
        <Button variant="outline" onClick={onCheck} disabled={checking}>
          {checking ? <Spinner aria-hidden /> : <RefreshCwIcon aria-hidden />}
          Check again
        </Button>
      </div>
      <ul className="flex flex-col gap-3" aria-live="polite">
        {items.map((it) => (
          <li key={it.label} className="flex gap-3">
            {it.done ? (
              <CheckCircle2Icon className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
            ) : (
              <CircleIcon className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />
            )}
            <div>
              <p className={cn(!it.done && 'text-muted-foreground')}>
                {it.label}
                <span className="sr-only">{it.done ? ': done' : ': not yet'}</span>
              </p>
              {!it.done && <p className="text-sm text-muted-foreground">{it.pending}</p>}
            </div>
          </li>
        ))}
      </ul>
      {view.status.mxUnchanged === false && (
        <p role="alert" className="flex gap-2 text-sm text-destructive">
          <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          Your email (MX) records changed since we first checked. If you did not mean to change them, put them back or
          reply to our latest email so we can help.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Last checked at {formatTime(view.checkedAt)}.{!view.status.live && ' We check again every 30 seconds while this page is open.'}
      </p>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </section>
  )
}

function CurrentRecords({ view }: { view: DnsView }) {
  const groups: Array<[string, DnsRecord[]]> = [
    [`Bare domain (${view.domain})`, view.current.apex],
    [`www.${view.domain}`, view.current.www],
    ['Email (MX), leave these as they are', view.current.mx],
  ]
  return (
    <Collapsible className="rounded-xl border border-border">
      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-4 py-3 text-left font-medium outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
        What your domain settings look like today
        <ChevronDownIcon className="size-4 transition-transform group-data-[panel-open]:rotate-180" aria-hidden />
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-4 border-t border-border px-4 py-3 text-sm">
        {view.nameservers.length > 0 && (
          <p>
            <span className="text-muted-foreground">Nameservers: </span>
            <span className="font-mono break-all">{view.nameservers.join(', ')}</span>
          </p>
        )}
        {groups.map(([title, recs]) => (
          <div key={title} className="flex flex-col gap-1">
            <p className="font-medium">{title}</p>
            {recs.length === 0 ? (
              <p className="text-muted-foreground">No records found.</p>
            ) : (
              <ul className="flex flex-col gap-1 font-mono text-xs">
                {recs.map((r, i) => (
                  <li key={i} className="break-all">
                    {r.type} {r.name} → {r.value}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}
